import {create} from 'zustand'
import {
  GENERAL_NOTIFICATION_TYPES,
  INotificationTypeCount,
  SIGNATURE_NOTIFICATION_TYPES,
} from '../types/notifications'

type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected'

type SidebarCounters = {
  unreadNotificationsCount: number
  openTicketsCount: number
  inboxUnreadCount: number
  policyChatUnreadCount: number
}

type NotificationCounters = {
  generalUnreadCount: number
  signatureUnreadCount: number
}

type RealtimeEvent = {
  type?: string
  payload?: Record<string, unknown>
}

type State = {
  connectionStatus: ConnectionStatus
  sidebarCounters: SidebarCounters
  notificationCounters: NotificationCounters
  lastEventType: string | null
  lastEventPayload: Record<string, any> | null
  lastEventTimestamp: number | null
}

type Actions = {
  reset: () => void
  setConnectionStatus: (status: ConnectionStatus) => void
  syncSidebarCounters: (counters: Partial<SidebarCounters>) => void
  syncUnreadNotificationsByType: (counts: INotificationTypeCount[]) => void
  handleSocketEvent: (event: RealtimeEvent) => void
  markNotificationRead: (notificationType?: string | null) => void
  markNotificationDeleted: (notificationType?: string | null, wasUnread?: boolean) => void
  markAllNotificationsRead: () => void
}

const initialSidebarCounters: SidebarCounters = {
  unreadNotificationsCount: 0,
  openTicketsCount: 0,
  inboxUnreadCount: 0,
  policyChatUnreadCount: 0,
}

const initialNotificationCounters: NotificationCounters = {
  generalUnreadCount: 0,
  signatureUnreadCount: 0,
}

const isSignatureNotificationType = (notificationType?: string | null) =>
  Boolean(notificationType && SIGNATURE_NOTIFICATION_TYPES.includes(notificationType as never))

const isGeneralNotificationType = (notificationType?: string | null) =>
  Boolean(notificationType && GENERAL_NOTIFICATION_TYPES.includes(notificationType as never))

const incrementNotificationCounters = (
  counters: NotificationCounters,
  notificationType?: string | null
): NotificationCounters => {
  if (isSignatureNotificationType(notificationType)) {
    return {...counters, signatureUnreadCount: counters.signatureUnreadCount + 1}
  }

  if (isGeneralNotificationType(notificationType)) {
    return {...counters, generalUnreadCount: counters.generalUnreadCount + 1}
  }

  return counters
}

const decrementNotificationCounters = (
  counters: NotificationCounters,
  notificationType?: string | null
): NotificationCounters => {
  if (isSignatureNotificationType(notificationType)) {
    return {
      ...counters,
      signatureUnreadCount: Math.max(0, counters.signatureUnreadCount - 1),
    }
  }

  if (isGeneralNotificationType(notificationType)) {
    return {
      ...counters,
      generalUnreadCount: Math.max(0, counters.generalUnreadCount - 1),
    }
  }

  return counters
}

const buildNotificationCounters = (counts: INotificationTypeCount[]): NotificationCounters => {
  return counts.reduce(
    (acc, item) => {
      if (isSignatureNotificationType(item.notificationType)) {
        acc.signatureUnreadCount += item.unreadCount || 0
      }

      if (isGeneralNotificationType(item.notificationType)) {
        acc.generalUnreadCount += item.unreadCount || 0
      }

      return acc
    },
    {...initialNotificationCounters}
  )
}

export const useRealtimeStore = create<State & Actions>((set) => ({
  connectionStatus: 'idle',
  sidebarCounters: initialSidebarCounters,
  notificationCounters: initialNotificationCounters,
  lastEventType: null,
  lastEventPayload: null,
  lastEventTimestamp: null,
  reset: () =>
    set(() => ({
      connectionStatus: 'idle',
      sidebarCounters: initialSidebarCounters,
      notificationCounters: initialNotificationCounters,
      lastEventType: null,
      lastEventPayload: null,
      lastEventTimestamp: null,
    })),
  setConnectionStatus: (connectionStatus) => set(() => ({connectionStatus})),
  syncSidebarCounters: (counters) =>
    set((state) => ({
      sidebarCounters: {
        ...state.sidebarCounters,
        ...counters,
      },
    })),
  syncUnreadNotificationsByType: (counts) =>
    set((state) => {
      const notificationCounters = buildNotificationCounters(counts)
      return {
        notificationCounters,
        sidebarCounters: {
          ...state.sidebarCounters,
          unreadNotificationsCount:
            notificationCounters.generalUnreadCount + notificationCounters.signatureUnreadCount,
        },
      }
    }),
  markNotificationRead: (notificationType) =>
    set((state) => ({
      notificationCounters: decrementNotificationCounters(
        state.notificationCounters,
        notificationType
      ),
      sidebarCounters: {
        ...state.sidebarCounters,
        unreadNotificationsCount: Math.max(0, state.sidebarCounters.unreadNotificationsCount - 1),
      },
      lastEventType: 'NOTIFICATION_READ',
    })),
  markNotificationDeleted: (notificationType, wasUnread = false) =>
    set((state) => {
      if (!wasUnread) {
        return {lastEventType: 'NOTIFICATION_DELETED'}
      }

      return {
        notificationCounters: decrementNotificationCounters(
          state.notificationCounters,
          notificationType
        ),
        sidebarCounters: {
          ...state.sidebarCounters,
          unreadNotificationsCount: Math.max(0, state.sidebarCounters.unreadNotificationsCount - 1),
        },
        lastEventType: 'NOTIFICATION_DELETED',
      }
    }),
  markAllNotificationsRead: () =>
    set((state) => ({
      notificationCounters: initialNotificationCounters,
      sidebarCounters: {
        ...state.sidebarCounters,
        unreadNotificationsCount: 0,
      },
      lastEventType: 'ALL_NOTIFICATIONS_READ',
    })),
  handleSocketEvent: (event) =>
    set((state) => {
      const payload = event.payload || {}
      const eventType = event.type || null
      const eventTimestamp = Date.now()

      switch (event.type) {
        case 'SIDEBAR_COUNTERS':
          return {
            sidebarCounters: {
              ...state.sidebarCounters,
              unreadNotificationsCount:
                Number(payload.unreadNotificationsCount) || 0,
              openTicketsCount: Number(payload.openTicketsCount) || 0,
              inboxUnreadCount: Number(payload.inboxUnreadCount) || 0,
              policyChatUnreadCount: Number(payload.policyChatUnreadCount) || 0,
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        case 'POLICYCHAT_UNREAD_COUNT':
          return {
            sidebarCounters: {
              ...state.sidebarCounters,
              policyChatUnreadCount: Number(payload.count) || 0,
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        case 'UNREAD_INBOX_COUNT':
          return {
            sidebarCounters: {
              ...state.sidebarCounters,
              inboxUnreadCount: Number(payload.count) || 0,
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        case 'OPEN_TICKETS_COUNT':
        case 'OPEN_TICKET_COUNT':
          return {
            sidebarCounters: {
              ...state.sidebarCounters,
              openTicketsCount: Number(payload.count) || 0,
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        case 'UNREAD_NOTIFICATIONS_COUNT':
          return {
            sidebarCounters: {
              ...state.sidebarCounters,
              unreadNotificationsCount: Number(payload.count) || 0,
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        case 'NEW_NOTIFICATION': {
          const notificationType = String(payload.notificationType || '')
          return {
            notificationCounters: incrementNotificationCounters(
              state.notificationCounters,
              notificationType
            ),
            sidebarCounters: {
              ...state.sidebarCounters,
              unreadNotificationsCount: state.sidebarCounters.unreadNotificationsCount + 1,
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        }
        case 'NOTIFICATION_READ': {
          const notificationType = String(payload.notificationType || '')
          const wasUnread = payload.wasUnread !== false

          if (!wasUnread) {
            return {lastEventType: eventType}
          }

          return {
            notificationCounters: decrementNotificationCounters(
              state.notificationCounters,
              notificationType
            ),
            sidebarCounters: {
              ...state.sidebarCounters,
              unreadNotificationsCount: Math.max(
                0,
                state.sidebarCounters.unreadNotificationsCount - 1
              ),
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        }
        case 'ALL_NOTIFICATIONS_READ':
          return {
            notificationCounters: initialNotificationCounters,
            sidebarCounters: {
              ...state.sidebarCounters,
              unreadNotificationsCount: 0,
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        case 'NOTIFICATION_DELETED': {
          const notificationType = String(payload.notificationType || '')
          const wasUnread = Boolean(payload.wasUnread)

          if (!wasUnread) {
            return {lastEventType: eventType}
          }

          return {
            notificationCounters: decrementNotificationCounters(
              state.notificationCounters,
              notificationType
            ),
            sidebarCounters: {
              ...state.sidebarCounters,
              unreadNotificationsCount: Math.max(
                0,
                state.sidebarCounters.unreadNotificationsCount - 1
              ),
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        }
        case 'NEW_TICKET':
          return {
            sidebarCounters: {
              ...state.sidebarCounters,
              openTicketsCount:
                payload.count !== undefined
                  ? Number(payload.count) || 0
                  : state.sidebarCounters.openTicketsCount + 1,
            },
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
        default:
          return {
            lastEventType: eventType,
            lastEventPayload: payload,
            lastEventTimestamp: eventTimestamp,
          }
      }
    }),
}))
